import { ChevronDown, Eye, EyeClosed, Hexagon } from 'lucide-react';
import { type ReactNode, type RefObject, useLayoutEffect, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { DealStageChips, DealSwatch } from '@/features/locations/components/DealStageChips';
import {
  CompetitorFilterOptions,
  PlaceChipSwatch,
  PlaceFilterChips,
  TrafficFilterChips,
} from '@/features/locations/components/LayerVisibilityFilters';
import { ToggleChip } from '@/features/locations/components/ToggleChip';
import { MarkKind, STATION_CHARGE_CHIPS, TRAFFIC_COLORS } from '@/features/locations/constants';
import { PLACE_FILTER_CHIPS, placeChipMaki } from '@/features/locations/constants/placeSiteTypes';
import {
  ELECTRA_CHIP_ICON,
  LOW_HPC_CHIP_ICON,
  STATION_ICONS,
} from '@/features/locations/constants/stationIcons';
import { type DealStageRow, inPipelineOrder } from '@/features/locations/hooks/useDealsStageFilter';
import type {
  LayerVisibilityHandlers,
  LayerVisibilityState,
} from '@/features/locations/hooks/useLayerVisibilityFilters';
import { buildClusterGlyphSvg } from '@/features/locations/utils/clusterChipMark';
import { ZONE_TYPE_COLORS, ZONE_TYPES, type ZoneType } from '@/features/locations/utils/zoneColors';
import { cn } from '@/libs/utils/cn';
import {
  MAP_LAYER_PANEL_WIDTH_CLASS,
  MAP_PILL_CLASS,
  MAP_PILL_NARROWED_CLASS,
} from './mapBarGeometry';

interface MapLayerFiltersProps {
  filterState: LayerVisibilityState;
  filterHandlers: LayerVisibilityHandlers;
  dealStages: DealStageRow[];
  onToggleDealStage: (stage: string) => void;
  onToggleAllDealStages: () => void;
  zonesVisible: boolean;
  onToggleZones: (visible: boolean) => void;
  visibleZoneTypes: readonly ZoneType[];
  onToggleZoneType: (type: ZoneType) => void;
}

/** Neutral step for a traffic band that is switched off, so the scale keeps its order. */
const UNSELECTED = '#D3DAD9';
const PREVIEW_MARKS = 3;

/** The chip SVGs stroke their disc white. On the white pill that reads as a border, so the pill crops to the disc. */
function pillMark(svg: string): string {
  return svg
    .replace(/<rect\b[^>]*\bstroke="#fff"[^>]*\/>/g, '')
    .replace(/viewBox="0 0 22 22"/, 'viewBox="1 1 20 20"');
}

/** Greys a legend mark whose layer is off, so the pill keeps its shape. */
const MUTED_MARK_CLASS = 'opacity-40 grayscale';

function SvgMark({
  svg,
  className = 'size-6',
  muted = false,
}: {
  svg: string;
  className?: string;
  muted?: boolean;
}) {
  return (
    <span
      data-muted={muted || undefined}
      className={cn('inline-flex shrink-0 [&>svg]:size-full', className, muted && MUTED_MARK_CLASS)}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: SVG built from static templates
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

/** Owner-style stack: three marks on top of each other, then the leftover count. */
function MarkStack({ marks, extra }: { marks: ReactNode[]; extra: number }) {
  return (
    <span aria-hidden className="flex shrink-0 items-center gap-0.5">
      <span className="flex -space-x-2">{marks}</span>
      {extra > 0 && <span className="text-[11px] font-medium">+{extra}</span>}
    </span>
  );
}

/** The territory-type legend, not ownership: H blue, R green, UH purple. */
function ZoneHexStack({
  className = 'size-5',
  muted = false,
  visibleTypes = ZONE_TYPES,
}: {
  className?: string;
  muted?: boolean;
  visibleTypes?: readonly ZoneType[];
}) {
  return (
    <span
      aria-hidden
      data-muted={muted || undefined}
      className={cn('inline-flex shrink-0 -space-x-2', muted && MUTED_MARK_CLASS)}
    >
      {ZONE_TYPES.map((type) => (
        <Hexagon
          key={type}
          className={cn(className, 'text-white', !visibleTypes.includes(type) && MUTED_MARK_CLASS)}
          style={{ fill: ZONE_TYPE_COLORS[type] }}
          strokeWidth={2}
        />
      ))}
    </span>
  );
}

const LOCATION_MARK = buildClusterGlyphSvg(MarkKind.LOCATION);

/** Pill wrappers by id, so a panel can span its neighbour. */
type PillRefs = Partial<Record<FilterId, HTMLDivElement | null>>;
type FilterId = 'map' | 'pois' | 'deals' | 'traffic' | 'competitors';
const PILL_GAP_PX = 8;

function LayerFilterPill({
  id,
  pills,
  spanWith,
  label,
  on,
  narrowed,
  onToggleLayer,
  legend,
  heading,
  subtitle,
  children,
}: {
  id: FilterId;
  pills: RefObject<PillRefs>;
  /** The neighbour the open panel covers too, and on which side it sits. */
  spanWith?: { id: FilterId; side: 'start' | 'end' };
  label: string;
  on: boolean;
  /** Some choices are off: the pill carries the same ring as a narrowed owner filter. */
  narrowed: boolean;
  onToggleLayer: () => void;
  legend: ReactNode;
  heading: string;
  subtitle?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState<number>();
  const selfRef = useRef<HTMLDivElement | null>(null);

  // Measured on every render while open: a toggled chip can change a pill's leftover count.
  useLayoutEffect(() => {
    if (!open || !selfRef.current) return;
    const other = spanWith ? pills.current[spanWith.id] : null;
    const next = selfRef.current.offsetWidth + (other ? PILL_GAP_PX + other.offsetWidth : 0);
    if (next > 0 && next !== width) setWidth(next);
  });

  return (
    <div
      ref={(el) => {
        selfRef.current = el;
        pills.current[id] = el;
      }}
      data-narrowed={on && narrowed ? 'true' : undefined}
      className="relative shrink-0"
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              MAP_PILL_CLASS,
              'min-w-28 pl-8',
              on && narrowed && MAP_PILL_NARROWED_CLASS,
              !on && 'opacity-45 grayscale',
            )}
          >
            {legend}
            <span className="min-w-0 flex-1 truncate text-left font-medium">{label}</span>
            <ChevronDown
              className={cn(
                'size-3 flex-none text-muted-foreground transition-transform',
                open && 'rotate-180',
              )}
              aria-hidden
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align={spanWith?.side ?? 'start'}
          sideOffset={8}
          style={width ? { width } : undefined}
          className={cn(
            !width && MAP_LAYER_PANEL_WIDTH_CLASS,
            'flex max-h-[min(50dvh,24rem)] flex-col gap-2 overflow-y-auto rounded-[10px] border-border p-3 shadow-modal',
          )}
        >
          <div className="flex min-w-0 flex-col">
            <p className="truncate text-[13px] font-bold leading-[18px] text-primary-700">
              {heading}
            </p>
            {subtitle && (
              <p className="truncate text-xs leading-4 text-muted-foreground">{subtitle}</p>
            )}
          </div>
          {children}
        </PopoverContent>
      </Popover>
      {/* Outside the trigger, so showing or hiding the layer never opens its chips. */}
      <button
        type="button"
        aria-pressed={on}
        aria-label={on ? `Hide ${label} layer` : `Show ${label} layer`}
        onClick={onToggleLayer}
        className="absolute top-1/2 left-1 z-10 flex size-6 -translate-y-1/2 items-center justify-center rounded-md text-[#0B2936] hover:bg-accent"
      >
        {on ? <Eye className="size-4" aria-hidden /> : <EyeClosed className="size-4" aria-hidden />}
      </button>
    </div>
  );
}

/**
 * One pill per layer. Map gathers the zones and locations chips. The eye on the
 * left shows or hides the layer; a hidden pill greys and still opens its chips. A
 * pill with some choices off carries a ring, like a narrowed owner filter.
 */
export function MapLayerFilters({
  filterState: state,
  filterHandlers: handlers,
  dealStages,
  onToggleDealStage,
  onToggleAllDealStages,
  zonesVisible,
  onToggleZones,
  visibleZoneTypes,
  onToggleZoneType,
}: MapLayerFiltersProps) {
  const mapOn = (zonesVisible && visibleZoneTypes.length > 0) || state.locationsVisible;
  const placesOn = PLACE_FILTER_CHIPS.filter((chip) => state.places[chip.id]);
  const dealsOn = inPipelineOrder(dealStages.filter((row) => row.visible));
  // Closed stages start hidden, so the Deals ring reads against that default, not against "all".
  const trafficBands = Object.values(TRAFFIC_COLORS);
  const trafficOn = trafficBands.filter((band) => state.traffic[band.id]);
  const competitorMarks = [
    ...(state.isElectraVisible ? [pillMark(ELECTRA_CHIP_ICON)] : []),
    ...(state.isLowHpcVisible ? [pillMark(LOW_HPC_CHIP_ICON)] : []),
    ...STATION_CHARGE_CHIPS.filter((item) => state.station[item.label]).map((item) =>
      pillMark(STATION_ICONS[item.rangeKey].icon),
    ),
  ];
  const competitorChoices = 2 + STATION_CHARGE_CHIPS.length;
  const competitorsOn = competitorMarks.length > 0 || state.isClustersVisible;
  const pills = useRef<PillRefs>({});
  // Each panel covers its pill and the next one; the last covers the one before it.
  const afterPois: FilterId = dealStages.length > 0 ? 'deals' : 'traffic';

  return (
    <>
      <LayerFilterPill
        id="map"
        pills={pills}
        label="Map"
        on={mapOn}
        narrowed={
          zonesVisible !== state.locationsVisible || visibleZoneTypes.length < ZONE_TYPES.length
        }
        onToggleLayer={() => {
          if (zonesVisible !== !mapOn || (!mapOn && visibleZoneTypes.length === 0)) {
            onToggleZones(!mapOn);
          }
          if (state.locationsVisible !== !mapOn) handlers.toggleLocations();
        }}
        legend={
          <span aria-hidden className="flex shrink-0 items-center gap-0.5">
            {/* Muted only while the pill is on: an off pill already fades as a whole. */}
            <ZoneHexStack muted={mapOn && !zonesVisible} visibleTypes={visibleZoneTypes} />
            <SvgMark
              svg={LOCATION_MARK}
              muted={mapOn && !state.locationsVisible}
              className="size-5"
            />
          </span>
        }
        heading="Map"
        subtitle={`${Number(zonesVisible) + Number(state.locationsVisible)} of 2`}
      >
        <div className="flex flex-row flex-wrap gap-2">
          <ToggleChip
            label="Zones"
            active={zonesVisible}
            onToggle={() => onToggleZones(!zonesVisible)}
            swatch={<ZoneHexStack className="size-4" />}
          />
          <ToggleChip
            label="Locations"
            active={state.locationsVisible}
            onToggle={handlers.toggleLocations}
            swatch={<SvgMark svg={LOCATION_MARK} className="size-4" />}
          />
        </div>
        <p className="text-xs text-muted-foreground">Zone types</p>
        <div className="flex flex-wrap gap-2">
          {ZONE_TYPES.map((type) => (
            <ToggleChip
              key={type}
              label={type}
              active={visibleZoneTypes.includes(type)}
              onToggle={() => onToggleZoneType(type)}
              swatch={
                <Hexagon
                  className="size-4 text-white"
                  style={{ fill: ZONE_TYPE_COLORS[type] }}
                  strokeWidth={2}
                />
              }
            />
          ))}
        </div>
      </LayerFilterPill>

      <LayerFilterPill
        id="pois"
        pills={pills}
        spanWith={{ id: afterPois, side: 'start' }}
        label="POIs"
        on={placesOn.length > 0}
        narrowed={placesOn.length < PLACE_FILTER_CHIPS.length}
        onToggleLayer={handlers.togglePlaceGroup}
        legend={
          <MarkStack
            extra={placesOn.length - PREVIEW_MARKS}
            marks={(placesOn.length > 0 ? placesOn : PLACE_FILTER_CHIPS)
              .slice(0, PREVIEW_MARKS)
              .map((chip) => (
                <PlaceChipSwatch
                  key={chip.id}
                  maki={placeChipMaki(chip)}
                  placeClass={chip.placeClass}
                  className="size-6"
                  plate={false}
                />
              ))}
          />
        }
        heading={
          placesOn.length === PLACE_FILTER_CHIPS.length
            ? 'All place types'
            : `${placesOn.length} of ${PLACE_FILTER_CHIPS.length} place types`
        }
      >
        <PlaceFilterChips state={state} handlers={handlers} />
      </LayerFilterPill>

      {dealStages.length > 0 && (
        <LayerFilterPill
          id="deals"
          pills={pills}
          spanWith={{ id: 'traffic', side: 'start' }}
          label="Deals"
          on={dealsOn.length > 0}
          narrowed={dealStages.some((row) => row.visible === row.style.hollow)}
          onToggleLayer={onToggleAllDealStages}
          legend={
            <MarkStack
              extra={dealsOn.length - PREVIEW_MARKS}
              marks={(dealsOn.length > 0 ? dealsOn : inPipelineOrder(dealStages))
                .slice(0, PREVIEW_MARKS)
                .map((row) => <DealSwatch key={row.stage} row={row} className="size-6" />)}
            />
          }
          heading="Deal stages"
          subtitle={`${dealsOn.length} of ${dealStages.length}`}
        >
          <DealStageChips stages={dealStages} onToggleStage={onToggleDealStage} />
        </LayerFilterPill>
      )}

      <LayerFilterPill
        id="traffic"
        pills={pills}
        spanWith={{ id: 'competitors', side: 'start' }}
        label="Traffic"
        on={trafficOn.length > 0}
        narrowed={trafficOn.length < trafficBands.length}
        onToggleLayer={handlers.toggleTrafficGroup}
        legend={
          <span aria-hidden className="flex shrink-0 items-center gap-0.5">
            {trafficBands.map((band) => (
              <span
                key={band.id}
                className="size-2.5 rounded-full"
                style={{ backgroundColor: state.traffic[band.id] ? band.color : UNSELECTED }}
              />
            ))}
          </span>
        }
        heading="Average traffic levels"
        subtitle={`${trafficOn.length} of ${trafficBands.length}`}
      >
        <TrafficFilterChips state={state} handlers={handlers} />
      </LayerFilterPill>

      <LayerFilterPill
        id="competitors"
        pills={pills}
        spanWith={{ id: 'traffic', side: 'end' }}
        label="Competitors"
        on={competitorsOn}
        narrowed={competitorMarks.length < competitorChoices}
        onToggleLayer={handlers.toggleStationGroup}
        legend={
          <MarkStack
            extra={competitorMarks.length - PREVIEW_MARKS}
            marks={(competitorMarks.length > 0
              ? competitorMarks
              : [ELECTRA_CHIP_ICON, LOW_HPC_CHIP_ICON, STATION_ICONS.RANGE_1_10.icon].map(pillMark)
            )
              .slice(0, PREVIEW_MARKS)
              .map((svg) => <SvgMark key={svg} svg={svg} />)}
          />
        }
        heading="Competitive intelligence"
        subtitle={`${competitorMarks.length} of ${competitorChoices}`}
      >
        <CompetitorFilterOptions state={state} handlers={handlers} />
      </LayerFilterPill>
    </>
  );
}
